// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Reset Password Page Route
 *
 * Handles the password reset completion flow when user clicks the email link.
 * Accepts a token query parameter and renders the ResetPassword component.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAuthState } from "@/providers/AuthProvider";
import { Logo } from "@/ui/logo";
import { LanguageSwitcher } from "@/ui/language-switcher";
import { ResetPassword } from "@/components/auth/ResetPassword";

export const Route = createFileRoute("/_app/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || "",
  }),
});

const QUOTES = [
  "vaultYourKeys",
  "onlyYouHoldTheKey",
  "zeroKnowledgeByDesign",
  "encryptedBeforeLeaving",
  "endToEndEncrypted",
  "weNeverSeePasswords",
  "oneMasterPassword",
  "strongEncryption",
  "yourPasswordsYourControl",
  "privateByDefault",
  "peaceOfMind",
  "truPrivacy",
];

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { status, isLoading } = useAuthState();
  const { t } = useTranslation("auth");
  const { token } = Route.useSearch();

  const randomQuote = useMemo(
    () => QUOTES[Math.floor(Math.random() * QUOTES.length)] ?? QUOTES[0]!,
    []
  );

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && status === "authenticated") {
      navigate({ to: "/dashboard" });
    }
  }, [isLoading, status, navigate]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full">
      {/* Left branding panel - hidden on mobile */}
      <div className="relative hidden h-full w-[50%] flex-col justify-between overflow-hidden bg-card p-10 lg:flex">
        <Link to="/" className="z-10 flex h-10 w-10 items-center gap-1">
          <Logo />
        </Link>

        <div className="z-10 flex flex-col items-start gap-3">
          <Lock className="h-5 w-5 text-primary" />
          <p className="text-lg sm:text-xl font-medium text-foreground">
            {t(`quotes.${randomQuote}`)}
          </p>
        </div>

        <div className="z-10 absolute top-4 end-4">
          <LanguageSwitcher />
        </div>

        <div className="base-grid absolute start-0 top-0 z-0 h-full w-full opacity-40" />
      </div>

      {/* Right panel - form */}
      <div className="flex h-full w-full flex-col border-s border-primary/5 bg-card lg:w-[50%]">
        <div className="absolute start-1/2 top-10 mx-auto flex -translate-x-1/2 transform lg:hidden">
          <Link to="/" className="z-10 flex h-10 flex-col items-center justify-center gap-2">
            <Logo />
          </Link>
        </div>

        <div className="mx-auto flex h-full w-full max-w-md flex-col items-center justify-center gap-6 px-4 py-8 overflow-y-auto">
          <ResetPassword
            token={token}
            onSuccess={() => navigate({ to: "/login" })}
            onNavigateToLogin={() => navigate({ to: "/login" })}
          />
        </div>
      </div>
    </div>
  );
}
